import { fetchAuthSession } from "aws-amplify/auth";
import { env } from "./env.js";

/**
 * API client for the Hearth backend. Wraps `fetch` with:
 *   - JWT attach via Amplify v6 fetchAuthSession() → access token
 *   - 401 retry once with forceRefresh
 *   - Hearth response envelope unpacking ({ data } / { error: {...} })
 *   - Typed `ApiError` thrown on non-2xx
 *
 * **Token choice:** access token, not ID token. The HTTP API JWT
 * authorizer accepts both (Cognito access tokens are validated by
 * `client_id`, ID tokens by `aud`). Pre-token-generation v2 enriches
 * BOTH tokens with `tenant_id`, `primary_household_id`, `role`,
 * `memberships`. Tests mirror this choice.
 *
 * **No retries beyond 401-once-with-refresh.** Network retries are the
 * caller's responsibility (TanStack Query handles this with built-in
 * retry semantics).
 */

export interface ApiErrorArgs {
  code: string;
  message: string;
  status: number;
  details?: unknown;
  requestId?: string | undefined;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(args: ApiErrorArgs) {
    super(args.message);
    this.name = "ApiError";
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
    this.requestId = args.requestId;
  }
}

interface ApiInit extends RequestInit {
  /** Set to false for public endpoints; defaults to true. */
  auth?: boolean;
}

interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: unknown;
    request_id?: string;
  };
}

interface SuccessEnvelope<T> {
  data: T;
}

async function getAccessToken(forceRefresh = false): Promise<string | undefined> {
  const session = await fetchAuthSession(forceRefresh ? { forceRefresh } : undefined);
  return session.tokens?.accessToken?.toString();
}

export async function apiFetch<T>(path: string, init: ApiInit = {}): Promise<T> {
  const url = `${env.apiBaseUrl}${path}`;
  const headers = new Headers(init.headers);
  if (!headers.has("content-type") && init.body) {
    headers.set("content-type", "application/json");
  }
  const auth = init.auth !== false;
  if (auth) {
    const token = await getAccessToken(false);
    if (token) headers.set("Authorization", token);
  }

  let res = await fetch(url, { ...init, headers });

  // Retry once on 401 with forced token refresh — Amplify normally
  // refreshes pro-actively, but a clock-skew or cache miss can produce
  // a one-off 401. forceRefresh: true makes Cognito issue a fresh
  // access token from the refresh token.
  if (res.status === 401 && auth) {
    const fresh = await getAccessToken(true);
    if (fresh) {
      headers.set("Authorization", fresh);
      res = await fetch(url, { ...init, headers });
    }
  }

  if (res.status === 204) {
    // No body for noContent responses.
    return undefined as T;
  }

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    parsed = {};
  }

  if (!res.ok) {
    const env_ = parsed as Partial<ErrorEnvelope>;
    const error = env_.error;
    throw new ApiError({
      code: error?.code ?? "UNKNOWN_ERROR",
      message: error?.message ?? res.statusText,
      status: res.status,
      details: error?.details,
      requestId: error?.request_id,
    });
  }

  return (parsed as SuccessEnvelope<T>).data;
}
